import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import { Moon, Sun, ArrowLeft } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Star, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tables } from '@/integrations/supabase/types';
import { trackFunnelStep, generateSessionId, type FunnelStep } from '@/lib/analytics/funnel-tracking';
import { AIReviewForm } from '@/components/reviews/AIReviewForm';

interface ReviewForm {
  id: string;
  business_id: string;
  title: string;
  active_status: boolean;
  enable_step_2?: boolean;
  ai_mode_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

// Branching rules for answer-based question flow
interface BranchingAction {
  action: 'goto_question' | 'skip_to_end' | 'show_investigation';
  targetQuestionId?: string;
}

interface BranchingRules {
  answers: {
    [answerText: string]: BranchingAction;
  };
}

interface Question extends Tables<'questions'> {
  options?: { id: string; value: string }[];
  visible_on_high_rating?: boolean;
  visible_on_low_rating?: boolean;
  branching_rules?: BranchingRules;
}

interface UITextConfig {
  ratingTitle: string;
  ratingSubtitle: string;
  ratingCTA: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailPlaceholderHigh?: string;
  emailPlaceholderLow?: string;
  consentText: string;
  consentTextLow?: string;
  continueButtonText: string;
  nextButtonText: string;
  formTitle: string;
  formSubtitle: string;
  formTitleHigh?: string;
  formSubtitleHigh?: string;
  formTitleLow?: string;
  formSubtitleLow?: string;
  nameLabel: string;
  namePlaceholder: string;
  submitButtonText: string;
  successTitle: string;
  successMessageHigh: string;
  successMessageLow: string;
}

const DEFAULT_UI_TEXT: UITextConfig = {
  ratingTitle: "Rate your experience",
  ratingSubtitle: "with {businessName}",
  ratingCTA: "Tap a star to continue",
  emailLabel: "Email Address",
  emailPlaceholder: "Enter your email for reward",
  consentText: "I agree to terms",
  continueButtonText: "Continue to Google Reviews",
  nextButtonText: "Next",
  formTitle: "Share Your Feedback",
  formSubtitle: "Help us improve by answering a few questions",
  formTitleHigh: "Share Your Experience",
  formSubtitleHigh: "We'd love to hear more about your excellent experience!",
  formTitleLow: "Share Your Feedback",
  formSubtitleLow: "Help us improve by answering a few questions",
  nameLabel: "Your Name (Optional)",
  namePlaceholder: "Enter your name",
  submitButtonText: "Submit Feedback",
  successTitle: "Thank You!",
  successMessageHigh: "Thank you for your excellent rating! We really appreciate your feedback.",
  successMessageLow: "Your feedback has been submitted successfully. Thank you for helping us improve!"
};

const PublicReviewForm: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  
  const [form, setForm] = useState<ReviewForm | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [business, setBusiness] = useState<any>(null);
  const [uiTextConfig, setUITextConfig] = useState<UITextConfig>(DEFAULT_UI_TEXT);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [currentStep, setCurrentStep] = useState(1); // 1: Rating, 2: Email (if reward), 3: Custom Form, 4: Success
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [answers, setAnswers] = useState<{[key: string]: any}>({});
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [sessionId] = useState(() => generateSessionId());
  const [emailConsent, setEmailConsent] = useState(false);

  // Quiz funnel branching state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionPath, setQuestionPath] = useState<string[]>([]); // Track which questions were shown
  const [skipToEnd, setSkipToEnd] = useState(false);

  // Check if any questions have branching rules (enables quiz mode)
  const hasBranchingRules = questions.some(q => q.branching_rules && Object.keys(q.branching_rules.answers || {}).length > 0);

  // Get visible questions based on rating
  const getVisibleQuestions = (): Question[] => {
    const isHighRating = rating >= (business?.review_redirect_star_threshold || 4);
    return questions.filter(q => {
      if (isHighRating) {
        return q.visible_on_high_rating !== false;
      } else {
        return q.visible_on_low_rating !== false;
      }
    });
  };

  // Evaluate branching rules for a given answer
  const evaluateBranching = (question: Question, answer: string | string[]): BranchingAction | null => {
    if (!question.branching_rules?.answers) return null;

    const answers = Array.isArray(answer) ? answer : [answer];

    // Check each answer against branching rules
    for (const ans of answers) {
      const rule = question.branching_rules.answers[ans];
      if (rule) {
        return rule;
      }
    }

    return null;
  };

  // Find next question based on branching or sequential order
  const findNextQuestion = (currentQuestion: Question, answer: string | string[]): { questionId: string | null; action: 'next' | 'skip_to_end' | 'goto' } => {
    const branchAction = evaluateBranching(currentQuestion, answer);
    const visibleQuestions = getVisibleQuestions();

    if (branchAction) {
      if (branchAction.action === 'skip_to_end') {
        return { questionId: null, action: 'skip_to_end' };
      }
      if (branchAction.action === 'goto_question' && branchAction.targetQuestionId) {
        // Verify target question exists and is visible
        const targetExists = visibleQuestions.some(q => q.id === branchAction.targetQuestionId);
        if (targetExists) {
          return { questionId: branchAction.targetQuestionId, action: 'goto' };
        }
      }
    }

    // Default: go to next sequential question
    const currentIndex = visibleQuestions.findIndex(q => q.id === currentQuestion.id);
    if (currentIndex < visibleQuestions.length - 1) {
      return { questionId: visibleQuestions[currentIndex + 1].id, action: 'next' };
    }

    return { questionId: null, action: 'next' }; // No more questions
  };

  // Handle answer change with branching evaluation (for quiz mode)
  const handleQuizAnswerChange = (questionId: string, value: string | string[]) => {
    handleAnswerChange(questionId, value);

    if (!hasBranchingRules) return;

    const visibleQuestions = getVisibleQuestions();
    const question = visibleQuestions.find(q => q.id === questionId);
    if (!question) return;

    // For multiple_choice (single select), move to next question after selection
    if (question.type === 'multiple_choice' && typeof value === 'string') {
      const { questionId: nextId, action } = findNextQuestion(question, value);

      if (action === 'skip_to_end') {
        setSkipToEnd(true);
        setQuestionPath(prev => [...prev, questionId]);
      } else if (nextId) {
        const nextIndex = visibleQuestions.findIndex(q => q.id === nextId);
        if (nextIndex !== -1) {
          setCurrentQuestionIndex(nextIndex);
          setQuestionPath(prev => [...prev, questionId]);
        }
      } else {
        // No more questions
        setQuestionPath(prev => [...prev, questionId]);
      }
    }
  };

  // Move to next question manually (for text/textarea questions in quiz mode)
  const handleQuizNext = () => {
    const visibleQuestions = getVisibleQuestions();
    const currentQuestion = visibleQuestions[currentQuestionIndex];

    if (!currentQuestion) return;

    const currentAnswer = answers[currentQuestion.id];
    const { questionId: nextId, action } = findNextQuestion(currentQuestion, currentAnswer || '');

    if (action === 'skip_to_end') {
      setSkipToEnd(true);
      setQuestionPath(prev => [...prev, currentQuestion.id]);
    } else if (nextId) {
      const nextIndex = visibleQuestions.findIndex(q => q.id === nextId);
      if (nextIndex !== -1) {
        setCurrentQuestionIndex(nextIndex);
        setQuestionPath(prev => [...prev, currentQuestion.id]);
      }
    } else {
      // No more questions - mark as complete
      setQuestionPath(prev => [...prev, currentQuestion.id]);
    }
  };

  // Check if we're at the last question or should show submit
  const isQuizComplete = (): boolean => {
    if (skipToEnd) return true;

    const visibleQuestions = getVisibleQuestions();
    if (currentQuestionIndex >= visibleQuestions.length - 1) {
      const lastQuestion = visibleQuestions[visibleQuestions.length - 1];
      return lastQuestion && questionPath.includes(lastQuestion.id);
    }
    return false;
  };

  useEffect(() => {
    fetchFormData();
  }, [formId]);

  // Note: Replaced browser-event-based abandonment tracking with assumption-based approach
  // Abandonment is now tracked immediately when entering step 2, then cancelled when user completes action

  const fetchFormData = async () => {
    if (!formId) {
      toast.error("Invalid form ID");
      navigate("/");
      return;
    }

    try {
      setLoading(true);

      // Fetch form with business data and questions
      const { data: formData, error: formError } = await supabase
        .from('review_forms')
        .select(`
          *,
          high_rating_email_enabled,
          high_rating_email_required,
          low_rating_email_enabled,
          low_rating_email_required,
          ai_mode_enabled,
          businesses!inner (
            id,
            name,
            review_redirect_star_threshold,
            google_place_id
          ),
          questions (*)
        `)
        .eq('id', formId)
        .eq('active_status', true)
        .single();

      if (formError) {
        if (formError.code === 'PGRST116') {
          toast.error("Form not found or is not active");
        } else {
          throw formError;
        }
        navigate("/");
        return;
      }

      if (!formData) {
        toast.error("Form not found");
        navigate("/");
        return;
      }

      setForm(formData);
      setBusiness(formData.businesses);
      setQuestions(formData.questions || []);
      
      // Load UI text configuration from form or use defaults
      if (formData.ui_text_config) {
        setUITextConfig({ ...DEFAULT_UI_TEXT, ...formData.ui_text_config } as UITextConfig);
      }

      // Track page loaded
      trackFunnelStep({
        sessionId,
        businessId: formData.businesses.id,
        stepName: 'page_loaded',
        stepData: { formId, formTitle: formData.title },
        formId: formId || null
      });

    } catch (error: any) {
      console.error("Error fetching form data:", error);
      toast.error("Failed to load form", {
        description: error.message || "Please try again later."
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process UI text with business name placeholder
  const processUIText = (text: string): string => {
    return text.replace(/{businessName}/g, business?.name || "this business");
  };

  const handleRatingClick = async (selectedRating: number) => {
    setRating(selectedRating);
    const threshold = business?.review_redirect_star_threshold || 4;
    
    // Track rating selection
    trackFunnelStep({
      sessionId,
      businessId: business.id,
      stepName: 'rating_selected',
      stepData: { rating: selectedRating, threshold },
      formId: formId || null
    });
    
    // Check if Step 2 is disabled
    if (form?.enable_step_2 === false) {
      if (selectedRating >= threshold) {
        // High rating - save review and redirect to Google Reviews if available
        if (business?.google_place_id) {
          // Save review to database before redirecting
          await saveReviewToDatabase(selectedRating, true);
          
          // Track Google redirect
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'redirected_to_google',
            stepData: { rating: selectedRating },
            formId: formId || null
          });
          
          // Redirect to Google Reviews
          const googleUrl = `https://search.google.com/local/writereview?placeid=${business.google_place_id}`;
          window.open(googleUrl, '_blank');
        }
        setCurrentStep(4); // Show success message
        return;
      } else {
        // Low rating - save review and go directly to success page
        await saveReviewToDatabase(selectedRating, false);
        setCurrentStep(4); // Show success message  
        return;
      }
    }
    
    // NEW LOGIC: Use separate high/low rating email settings
    const isHighRating = rating >= (business?.review_redirect_star_threshold || 4);
    
    if (isHighRating && form?.high_rating_email_enabled) {
      // High rating with email collection enabled
      setCurrentStep(2);
    } else if (!isHighRating && form?.low_rating_email_enabled) {
      // Low rating with email collection enabled  
      setCurrentStep(2);
    } else if (form?.enable_step_2) {
      // Step 2 enabled but no email collection for this rating
      setCurrentStep(2); // Still go to step 2 for questions only
    } else {
      // Step 2 is disabled or no email collection for this rating
      // Determine action based on rating and Google Place ID availability  
      if (isHighRating) {
        // High rating - DIRECT to Google (skip step2 to reduce friction)
        if (business?.google_place_id) {
          // Save review to database before redirecting
          await saveReviewToDatabase(selectedRating, true);
          
          // Track Google redirect
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'redirected_to_google',
            stepData: { rating: selectedRating },
            formId: formId || null
          });
          
          // Redirect to Google Reviews
          const googleUrl = `https://search.google.com/local/writereview?placeid=${business.google_place_id}`;
          window.open(googleUrl, '_blank');
          setCurrentStep(4); // Show success message
          return;
        }
        // No Google Place ID - still show success (high ratings don't need internal feedback)
        setCurrentStep(4);
        return;
      } else {
        // Low rating - go to step 2 for internal feedback
        setCurrentStep(2);
        
      }
    }
  };


  const handleEmailStep = () => {
    // This function is no longer used as email and form are combined in step 2
    // when rewards are enabled. Keep for backward compatibility.
    if (rating >= (business?.review_redirect_star_threshold || 4)) {
      setCurrentStep(4); // Success (would have redirected to Google)
    } else {
      setCurrentStep(3); // Go to custom form
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Validate email if required based on rating and settings
    const isHighRating = rating >= (business?.review_redirect_star_threshold || 4);
    const emailRequired = isHighRating ? form?.high_rating_email_required : form?.low_rating_email_required;
    const emailEnabled = isHighRating ? form?.high_rating_email_enabled : form?.low_rating_email_enabled;

    if (emailEnabled && emailRequired && !customerEmail.trim()) {
      newErrors['email'] = 'Email is required';
    }

    // Validate email consent checkbox when email collection is enabled
    if (emailEnabled && !emailConsent) {
      newErrors['emailConsent'] = 'You must agree to receive promotional emails';
    }

    // Validate questions
    questions.forEach(question => {
      if (question.required) {
        const answer = answers[question.id];
        if (!answer || (typeof answer === 'string' && !answer.trim())) {
          newErrors[question.id] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to save review data to database
  const saveReviewToDatabase = async (reviewRating: number, isRedirected: boolean = false) => {
    try {
      // Build content from custom answers (only include filled answers)
      const contentParts: string[] = [];
      questions.forEach(question => {
        const answer = answers[question.id];
        if (answer && question.text) {
          if (Array.isArray(answer)) {
            if (answer.length > 0) {
              contentParts.push(`${question.text}: ${answer.join(', ')}`);
            }
          } else if (typeof answer === 'string' && answer.trim()) {
            contentParts.push(`${question.text}: ${answer}`);
          }
        }
      });
      
      // For redirected reviews, create a basic content if no custom answers
      const reviewContent = contentParts.length > 0 
        ? contentParts.join('\n')
        : isRedirected 
          ? `Customer gave a ${reviewRating}-star rating and was redirected to Google Reviews`
          : '';

      // Create review entry
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .insert({
          business_id: business.id,
          customer_name: customerName.trim() || null,
          customer_email: customerEmail.trim() || null,
          content: reviewContent,
          rating: reviewRating,
          source: isRedirected ? 'Custom Form (Redirected to Google)' : 'Custom Form',
          custom_answers: answers,
          form_id: formId,
          session_id: sessionId,
          tag_id: null,
          published: true // All reviews should be visible in dashboard
        })
        .select()
        .single();

      if (reviewError) {
        console.error("Error saving review:", reviewError);
        return null;
      }

      // Note: Customers table is populated separately via existing customer lookup logic

      return reviewData;
    } catch (error) {
      console.error("Error in saveReviewToDatabase:", error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      const threshold = business?.review_redirect_star_threshold || 4;
      const isHighRating = rating >= threshold;
      const shouldRedirectToGoogle = isHighRating && form?.high_rating_email_enabled && business?.google_place_id;
      
      // Save review to database using helper function
      const reviewData = await saveReviewToDatabase(rating || 5, shouldRedirectToGoogle);
      
      if (!reviewData) {
        throw new Error("Failed to save review to database");
      }

      // Track email interaction based on email collection settings
      const emailEnabled = isHighRating ? form?.high_rating_email_enabled : form?.low_rating_email_enabled;
      if (emailEnabled) {
        if (customerEmail.trim()) {
          // Track email entered
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'email_entered',
            stepData: { 
              rating: rating || 5,
              emailEntered: true,
              ratingType: 'high'
            },
            formId: formId || null
          });
          
          // Trigger reward email for high ratings with email
          if (isHighRating && form?.high_rating_email_enabled) {
            try {
              const { data, error } = await supabase.functions.invoke('send-reward-email', {
                body: {
                  reviewId: reviewData.id,
                  customerEmail: customerEmail.trim(),
                  customerName: customerName.trim() || undefined,
                  sessionId: sessionId
                }
              });
              
              if (error) {
                if (!error.message?.includes('already sent')) {
                  console.warn('Failed to send reward email:', error);
                }
              } else if (data?.success) {
                console.log('Reward email sent successfully:', data.emailId);
                // Note: The send-reward-email function handles funnel tracking internally
              }
            } catch (emailError) {
              // Don't break the flow if reward email fails
              console.warn('Reward email request failed:', emailError);
            }
          }
        } else {
          // Track email skipped
          trackFunnelStep({
            sessionId,
            businessId: business.id,
            stepName: 'email_skipped',
            stepData: { 
              rating: rating || 5,
              emailSkipped: true,
              ratingType: 'high'
            },
            formId: formId || null
          });
        }
      }

      // Track review submission (ONLY for cases where no email action taken)
      const hasEmailAction = emailEnabled; // Use the calculated emailEnabled from above
      
      if (!hasEmailAction) {
        // Track review_submitted
        trackFunnelStep({
          sessionId,
          businessId: business.id,
          stepName: 'review_submitted',
          stepData: { 
            rating: rating || 5,
            hasCustomAnswers: Object.keys(answers).length > 0,
            customerName: customerName.trim() || null,
            customerEmail: customerEmail.trim() || null,
            ratingType: isHighRating ? 'high' : 'low'
          },
          formId: formId || null
        });
      }
      // High ratings with rewards already tracked email_entered/email_skipped above

      // Handle Google redirect for high ratings with rewards enabled
      if (shouldRedirectToGoogle) {
        // Track Google redirect
        trackFunnelStep({
          sessionId,
          businessId: business.id,
          stepName: 'redirected_to_google',
          stepData: { rating: rating || 5, fromRewardFlow: true },
          formId: formId || null
        });
        
        // Redirect to Google Reviews
        const googleUrl = `https://search.google.com/local/writereview?placeid=${business.google_place_id}`;
        window.open(googleUrl, '_blank');
      }

      toast.success("Thank you for your feedback!", {
        description: "Your review has been submitted successfully."
      });

      // Move to success step
      setCurrentStep(4);

    } catch (error: any) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review", {
        description: error.message || "Please try again."
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const error = errors[question.id];
    
    switch (question.type) {
      case 'text':
        return (
          <div key={question.id} className="space-y-2">
            <Label htmlFor={question.id}>
              {question.text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder || "Your answer..."}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={question.id} className="space-y-2">
            <Label htmlFor={question.id}>
              {question.text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={question.id}
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={question.placeholder || "Your detailed response..."}
              rows={4}
              className={error ? 'border-red-500' : ''}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'rating':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {question.text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="flex justify-start space-x-1 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={24}
                  className={`cursor-pointer transition-all duration-200 ${
                    ((answers[question.id] as number) >= star)
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300 hover:text-yellow-200"
                  }`}
                  onClick={() => handleAnswerChange(question.id, star)}
                />
              ))}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'multiple_choice':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {question.text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={answers[question.id] || ''}
              onValueChange={(value) => {
                // Use quiz handler for branching, regular handler otherwise
                if (hasBranchingRules && question.branching_rules) {
                  handleQuizAnswerChange(question.id, value);
                } else {
                  handleAnswerChange(question.id, value);
                }
              }}
            >
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value || "Option text"} id={`${question.id}-${option.id}`} />
                  <Label htmlFor={`${question.id}-${option.id}`}>{option.value || "Option text"}</Label>
                </div>
              ))}
            </RadioGroup>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'checkboxes':
        return (
          <div key={question.id} className="space-y-2">
            <Label>
              {question.text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {(question.options || []).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${question.id}-${option.id}`}
                    checked={(answers[question.id] || []).includes(option.value || "Option text")}
                    onCheckedChange={(checked) => {
                      const currentAnswers = answers[question.id] || [];
                      if (checked) {
                        handleAnswerChange(question.id, [...currentAnswers, option.value || "Option text"]);
                      } else {
                        handleAnswerChange(question.id, currentAnswers.filter((v: string) => v !== (option.value || "Option text")));
                      }
                    }}
                  />
                  <Label htmlFor={`${question.id}-${option.id}`}>{option.value || "Option text"}</Label>
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <div className="text-lg font-medium">Loading form...</div>
        </div>
      </div>
    );
  }

  if (!form || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-lg font-medium mb-2">Form not found</div>
            <p className="text-muted-foreground mb-4">
              This form may have been deactivated or removed.
            </p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // AI Mode: Render AI-powered review form
  if (form.ai_mode_enabled) {
    return (
      <AIReviewForm
        formId={form.id}
        businessId={business.id}
        businessName={business.name}
        starThreshold={business.review_redirect_star_threshold || 4}
        googlePlaceId={business.google_place_id}
        uiTextConfig={{
          ratingTitle: uiTextConfig.ratingTitle,
          ratingSubtitle: uiTextConfig.ratingSubtitle,
          successTitle: uiTextConfig.successTitle,
          successMessageHigh: uiTextConfig.successMessageHigh,
          successMessageLow: uiTextConfig.successMessageLow,
        }}
      />
    );
  }

  // Handle back button
  const handleGoBack = () => {
    if (currentStep > 1) {
      // Reset rating and form data when going back
      setRating(0);
      setHoveredRating(0);
      setCustomerName('');
      setCustomerEmail('');
      setEmailConsent(false);
      setAnswers({});
      setErrors({});

      // Reset quiz funnel state
      setCurrentQuestionIndex(0);
      setQuestionPath([]);
      setSkipToEnd(false);

      // Always go back to step 1 from any other step
      // since step 2 is the combined form step when rewards are enabled
      // and step 3 is only used when rewards are disabled
      setCurrentStep(1);
    }
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Main Content */}
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <Card>
          <CardContent className="pt-6">
            {/* Step 1: Rating */}
            {currentStep === 1 && (
              <div className="text-center space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">{uiTextConfig.ratingTitle}</h3>
                  <p className="text-muted-foreground mb-6">{processUIText(uiTextConfig.ratingSubtitle)}</p>
                </div>
                
                <div className="flex justify-center space-x-2 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={48}
                      className={`cursor-pointer transition-all duration-200 ${
                        (rating >= star || hoveredRating >= star)
                          ? "text-yellow-400 fill-yellow-400 scale-110"
                          : "text-gray-300 hover:text-yellow-200"
                      }`}
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">{uiTextConfig.ratingCTA}</p>
              </div>
            )}

            {/* Step 2: Email Collection + Form (combined when rewards enabled) */}
            {currentStep === 2 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Collection Section (based on rating and email settings) */}
                {((rating >= (business?.review_redirect_star_threshold || 4) && form?.high_rating_email_enabled) ||
                  (rating < (business?.review_redirect_star_threshold || 4) && form?.low_rating_email_enabled)) ? (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold mb-2">
                        {rating >= (business?.review_redirect_star_threshold || 4)
                          ? uiTextConfig.formTitleHigh
                          : uiTextConfig.formTitleLow}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {rating >= (business?.review_redirect_star_threshold || 4)
                          ? uiTextConfig.formSubtitleHigh
                          : uiTextConfig.formSubtitleLow}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="customer-email" className="font-semibold">{uiTextConfig.emailLabel}</Label>
                      <Input
                        id="customer-email"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder={
                          rating >= (business?.review_redirect_star_threshold || 4)
                            ? (uiTextConfig.emailPlaceholderHigh || uiTextConfig.emailPlaceholder)
                            : (uiTextConfig.emailPlaceholderLow || uiTextConfig.emailPlaceholder)
                        }
                        className="mt-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="email-consent"
                          className="mt-0.5"
                          checked={emailConsent}
                          onCheckedChange={(checked) => {
                            setEmailConsent(checked as boolean);
                            if (errors.emailConsent) {
                              setErrors(prev => ({ ...prev, emailConsent: '' }));
                            }
                          }}
                        />
                        <Label htmlFor="email-consent" className="text-sm text-muted-foreground cursor-pointer">
                          {(() => {
                            const consentText = rating >= (business?.review_redirect_star_threshold || 4)
                              ? uiTextConfig.consentText
                              : (uiTextConfig.consentTextLow || uiTextConfig.consentText);

                            // Split the text at the last word to add privacy link
                            const words = consentText.trim().split(/\s+/);
                            const lastWord = words[words.length - 1];
                            const beforeLastWord = words.slice(0, -1).join(' ');

                            const businessId = business?.id || form?.business_id;
                            return (
                              <>
                                {beforeLastWord}{' '}
                                <a
                                  href={businessId ? `/privacy/${businessId}` : "/privacy"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary underline hover:text-primary/80"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {lastWord}
                                </a>
                                {' *'}
                              </>
                            );
                          })()}
                        </Label>
                      </div>
                      {errors.emailConsent && (
                        <p className="text-sm text-red-500 ml-6">{errors.emailConsent}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Show form header when email collection is disabled */
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold mb-2">
                      {rating >= (business?.review_redirect_star_threshold || 4)
                        ? (uiTextConfig.formTitleHigh || uiTextConfig.formTitle)
                        : (uiTextConfig.formTitleLow || uiTextConfig.formTitle)}
                    </h3>
                    <p className="text-muted-foreground">
                      {rating >= (business?.review_redirect_star_threshold || 4)
                        ? (uiTextConfig.formSubtitleHigh || uiTextConfig.formSubtitle)
                        : (uiTextConfig.formSubtitleLow || uiTextConfig.formSubtitle)}
                    </p>
                  </div>
                )}

                {/* Form Fields - Quiz mode or show all based on branching rules */}
                <div className="space-y-6">
                  {hasBranchingRules ? (
                    /* Quiz Mode: Show one question at a time with branching */
                    <>
                      {(() => {
                        const visibleQuestions = getVisibleQuestions();
                        const currentQuestion = visibleQuestions[currentQuestionIndex];

                        if (!currentQuestion || skipToEnd || isQuizComplete()) {
                          return null;
                        }

                        return (
                          <div className="space-y-4">
                            {/* Progress indicator */}
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                              <span>Question {currentQuestionIndex + 1} of {visibleQuestions.length}</span>
                              {questionPath.length > 0 && (
                                <span>{questionPath.length} answered</span>
                              )}
                            </div>

                            {/* Render current question */}
                            {renderQuestion(currentQuestion)}

                            {/* Next button for text/textarea questions (multiple_choice auto-advances) */}
                            {(currentQuestion.type === 'text' || currentQuestion.type === 'textarea' || currentQuestion.type === 'rating') && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleQuizNext}
                                disabled={currentQuestion.required && !answers[currentQuestion.id]}
                                className="w-full"
                              >
                                {currentQuestionIndex < visibleQuestions.length - 1 ? uiTextConfig.nextButtonText : 'Continue to Submit'}
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    /* Standard Mode: Show all questions at once */
                    <>
                      {questions
                        .filter(q => {
                          const isHighRating = rating >= (business?.review_redirect_star_threshold || 4);
                          if (isHighRating) {
                            return q.visible_on_high_rating !== false;
                          } else {
                            return q.visible_on_low_rating !== false;
                          }
                        })
                        .map(renderQuestion)}
                    </>
                  )}
                </div>

                {/* Submit Button - Show when quiz is complete or in standard mode */}
                {(!hasBranchingRules || skipToEnd || isQuizComplete() || (hasBranchingRules && getVisibleQuestions().length === questionPath.length)) && (
                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full"
                      size="lg"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        uiTextConfig.submitButtonText
                      )}
                    </Button>
                  </div>
                )}
              </form>
            )}

            {/* Step 3: Custom Form */}
            {currentStep === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold mb-2">
                    {rating >= (business?.review_redirect_star_threshold || 4)
                      ? (uiTextConfig.formTitleHigh || uiTextConfig.formTitle)
                      : (uiTextConfig.formTitleLow || uiTextConfig.formTitle)}
                  </h3>
                  <p className="text-muted-foreground">
                    {rating >= (business?.review_redirect_star_threshold || 4)
                      ? (uiTextConfig.formSubtitleHigh || uiTextConfig.formSubtitle)
                      : (uiTextConfig.formSubtitleLow || uiTextConfig.formSubtitle)}
                  </p>
                </div>

                {/* Custom Questions */}
                {questions.map(renderQuestion)}

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full"
                    size="lg"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      uiTextConfig.submitButtonText
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 4: Success */}
            {currentStep === 4 && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <div className="text-3xl">✅</div>
                </div>
                <h3 className="text-xl font-semibold mb-2">{uiTextConfig.successTitle}</h3>
                <p className="text-muted-foreground">
                  {rating >= (business?.review_redirect_star_threshold || 4) 
                    ? uiTextConfig.successMessageHigh
                    : uiTextConfig.successMessageLow
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Back Button - Bottom Right - Hide on success step (step 4) */}
        {currentStep > 1 && currentStep < 4 && (
          <button
            onClick={handleGoBack}
            className="fixed bottom-6 right-6 w-12 h-12 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center transition-all shadow-lg z-50"
            title="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-primary-foreground" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PublicReviewForm;